import { apiFetch } from "@/lib/api-fetch";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import type {
  CadastrarClienteOmieBody,
  ClienteOmieGuardado,
  EnderecoDoCep,
  OmieEmpresaChave,
  RascunhoClienteOmie,
  AcaoDoCiclo,
  CicloBody,
  CreatePropostaBody,
  DocumentoDownloadResponse,
  FormatoDocumento,
  GenerateDocumentoResponse,
  GeneratedInvite,
  InviteReceiptText,
  InviteReceiptTextBody,
  InviteReceiptTextPatch,
  InviteReceiptTextsResponse,
  ListarPropostasParams,
  NovoPeriodoBody,
  OmieCandidatosResponse,
  OmieClientesResponse,
  OmieCompetencia,
  OmieCompetenciaBody,
  OmieCompetenciasResponse,
  OmieCopiarParaCadastroBody,
  OmieCopiarParaCadastroResposta,
  OmieCliente,
  OmieStatusResponse,
  OmieSyncResponse,
  OmieTipoCadastro,
  OmieVinculoBody,
  OmieVinculoFiltro,
  ListarOsParams,
  OrdensServicoPreviaResponse,
  OsHistoricoResponse,
  SincronizarOsResponse,
  AgrupamentoOsBody,
  EnviarOsBody,
  EnviarOsResponse,
  PatchAgenciaDaPropostaBody,
  PatchFreelancerDaPropostaBody,
  PropostaFreelancerDocumentos,
  PatchPropostaBody,
  PropostaAgenciaDetail,
  PropostaDetail,
  PropostaPreviewResponse,
  PropostasListResponse,
  PropostasPorProjetoResponse,
  ProjetosElegiveisResponse,
  CatalogoDoModelo,
  ConteudoDoModelo,
  SecaoDoModelo,
  TemplateResumo,
  TemplateTipo,
  VarianteDoModelo,
  DocumentoEditavel,
  EdicaoDeTrecho,
  DisponibilidadeCodigoProposta,
} from "@/types/finance";

const BASE = SUPABASE_URL.replace(/\/$/, "");

function url(path: string): string {
  return `${BASE}${path}`;
}

export const financeApi = {
  getOmieStatus(): Promise<OmieStatusResponse> {
    return apiFetch(url("/finance/omie/status"));
  },

  sincronizarClientes(): Promise<OmieSyncResponse> {
    return apiFetch(url("/finance/omie/sync-clientes"), { method: "POST" });
  },

  listarClientesOmie(params: {
    tipo?: OmieTipoCadastro;
    vinculo: OmieVinculoFiltro;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<OmieClientesResponse> {
    const q = new URLSearchParams();
    if (params.tipo) q.set("tipo", params.tipo);
    q.set("vinculo", params.vinculo);
    if (params.q) q.set("q", params.q);
    q.set("page", String(params.page ?? 1));
    q.set("page_size", String(params.page_size ?? 24));
    return apiFetch(url(`/finance/omie/clientes?${q.toString()}`));
  },

  listarCandidatos(params: {
    tipo: OmieTipoCadastro;
    q?: string;
  }): Promise<OmieCandidatosResponse> {
    const q = new URLSearchParams();
    q.set("tipo", params.tipo);
    if (params.q) q.set("q", params.q);
    return apiFetch(url(`/finance/omie/candidatos?${q.toString()}`));
  },

  vincularCliente(clienteId: string, body: OmieVinculoBody): Promise<OmieCliente> {
    return apiFetch(url(`/finance/omie/clientes/${clienteId}/vinculo`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  conferirVinculo(clienteId: string): Promise<OmieCliente> {
    return apiFetch(url(`/finance/omie/clientes/${clienteId}/vinculo/conferir`), { method: "POST" });
  },

  desvincularCliente(clienteId: string): Promise<OmieCliente> {
    return apiFetch(url(`/finance/omie/clientes/${clienteId}/vinculo`), {
      method: "DELETE",
    });
  },

  copiarParaCadastro(
    clienteId: string,
    body: OmieCopiarParaCadastroBody,
  ): Promise<OmieCopiarParaCadastroResposta> {
    return apiFetch(url(`/finance/omie/clientes/${clienteId}/copiar-para-cadastro`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  getCompetencias(): Promise<OmieCompetenciasResponse> {
    return apiFetch(url("/finance/omie/competencias"));
  },

  atualizarCompetencia(
    competencia: string,
    body: OmieCompetenciaBody,
  ): Promise<OmieCompetencia> {
    return apiFetch(url(`/finance/omie/competencias/${encodeURIComponent(competencia)}`), {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  listInviteReceiptTexts(params: {
    agencia_id?: string;
    incluir_inativos?: boolean;
  }): Promise<InviteReceiptTextsResponse> {
    const q = new URLSearchParams();
    if (params.agencia_id) q.set("agencia_id", params.agencia_id);
    if (params.incluir_inativos) q.set("incluir_inativos", "true");
    return apiFetch(url(`/finance/invite-textos?${q.toString()}`));
  },

  createInviteReceiptText(body: InviteReceiptTextBody): Promise<InviteReceiptText> {
    return apiFetch(url("/finance/invite-textos"), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  updateInviteReceiptText(textId: string, body: InviteReceiptTextPatch): Promise<InviteReceiptText> {
    return apiFetch(url(`/finance/invite-textos/${textId}`), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  getTemplate(tipo: TemplateTipo, variante: VarianteDoModelo): Promise<TemplateResumo> {
    return apiFetch(url(`/finance/templates/${tipo}?variante=${variante}`));
  },

  getTemplateCatalogo(tipo: TemplateTipo, variante: VarianteDoModelo): Promise<CatalogoDoModelo> {
    return apiFetch(url(`/finance/templates/${tipo}/catalogo?variante=${variante}`));
  },

  getTemplateConteudo(
    tipo: TemplateTipo,
    variante: VarianteDoModelo,
    versaoId: string | null,
  ): Promise<ConteudoDoModelo> {
    const q = new URLSearchParams();
    q.set("variante", variante);
    if (versaoId) q.set("versao_id", versaoId);
    return apiFetch(url(`/finance/templates/${tipo}/conteudo?${q.toString()}`));
  },

  salvarVersaoTemplate(
    tipo: TemplateTipo,
    variante: VarianteDoModelo,
    body: { secoes: SecaoDoModelo[]; observacao?: string },
  ): Promise<TemplateResumo> {
    return apiFetch(url(`/finance/templates/${tipo}/versoes`), {
      method: "POST",
      body: JSON.stringify({ ...body, variante }),
    });
  },

  ativarVersaoTemplate(
    tipo: TemplateTipo,
    variante: VarianteDoModelo,
    versaoId: string | null,
  ): Promise<TemplateResumo> {
    return apiFetch(url(`/finance/templates/${tipo}/ativar`), {
      method: "POST",
      body: JSON.stringify({ versao_id: versaoId, variante }),
    });
  },

  previaTemplate(
    tipo: TemplateTipo,
    body: { secoes: SecaoDoModelo[]; proposta_id?: string },
  ): Promise<PropostaPreviewResponse> {
    return apiFetch(url(`/finance/templates/${tipo}/previa`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  listarProjetosElegiveis(agenciaId: string): Promise<ProjetosElegiveisResponse> {
    const q = new URLSearchParams();
    q.set("agencia_id", agenciaId);
    return apiFetch(url(`/finance/proposals/projetos-elegiveis?${q.toString()}`));
  },

  listarPropostasPorProjeto(controleProjetoIds: string[]): Promise<PropostasPorProjetoResponse> {
    const q = new URLSearchParams();
    q.set("controle_projeto_ids", controleProjetoIds.join(","));
    return apiFetch(url(`/finance/proposals/por-projeto?${q.toString()}`));
  },

  criarProposta(body: CreatePropostaBody): Promise<PropostaDetail> {
    return apiFetch(url("/finance/proposals"), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  listarPropostas(params: ListarPropostasParams): Promise<PropostasListResponse> {
    const q = new URLSearchParams();
    if (params.agencia_id) q.set("agencia_id", params.agencia_id);
    if (params.status) q.set("status", params.status);
    if (params.competencia) q.set("competencia", params.competencia);
    if (params.q) q.set("q", params.q);
    q.set("page", String(params.page ?? 1));
    q.set("page_size", String(params.page_size ?? 24));
    return apiFetch(url(`/finance/proposals?${q.toString()}`));
  },

  getProposta(propostaId: string): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}`));
  },

  verificarCodigoDaProposta(propostaId: string, codigo: string): Promise<DisponibilidadeCodigoProposta> {
    const q = new URLSearchParams({ codigo });
    return apiFetch(url(`/finance/proposals/${propostaId}/codigo-disponivel?${q.toString()}`));
  },

  atualizarProposta(propostaId: string, body: PatchPropostaBody): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}`), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  adicionarItens(propostaId: string, controleProjetoIds: string[]): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}/itens`), {
      method: "POST",
      body: JSON.stringify({ controle_projeto_ids: controleProjetoIds }),
    });
  },

  adicionarPeriodoNovo(propostaId: string, body: NovoPeriodoBody): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}/periodos`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  removerItem(propostaId: string, itemId: string): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}/itens/${itemId}`), {
      method: "DELETE",
    });
  },

  gerarDocumento(propostaId: string): Promise<GenerateDocumentoResponse> {
    return apiFetch(url(`/finance/proposals/${propostaId}/generate`), {
      method: "POST",
    });
  },

  gerarInvite(propostaId: string, freelancerId: string): Promise<GeneratedInvite> {
    return apiFetch(url(`/finance/proposals/${propostaId}/invites/${freelancerId}/generate`), {
      method: "POST",
    });
  },

  getPropostaPreview(propostaId: string, edicao = false): Promise<PropostaPreviewResponse> {
    return apiFetch(url(`/finance/proposals/${propostaId}/preview${edicao ? "?edicao=true" : ""}`));
  },

  salvarTrechoDoDocumento(
    propostaId: string,
    documento: DocumentoEditavel,
    secaoId: string,
    edicao: EdicaoDeTrecho,
  ): Promise<{ modelo: string; trechos_editados: number }> {
    return apiFetch(url(`/finance/proposals/${propostaId}/documento/trechos/${encodeURIComponent(secaoId)}`), {
      method: "PUT",
      body: JSON.stringify({
        documento: documento.tipo,
        freelancer_id: documento.tipo === "invite" ? documento.freelancerId : null,
        ...edicao,
      }),
    });
  },

  restaurarTrechoDoDocumento(
    propostaId: string,
    documento: DocumentoEditavel,
    secaoId: string,
  ): Promise<{ modelo: string; trechos_editados: number }> {
    const q = new URLSearchParams();
    q.set("documento", documento.tipo);
    if (documento.tipo === "invite") q.set("freelancer_id", documento.freelancerId);
    return apiFetch(
      url(`/finance/proposals/${propostaId}/documento/trechos/${encodeURIComponent(secaoId)}?${q.toString()}`),
      { method: "DELETE" },
    );
  },

  getDocumentoDownload(
    propostaId: string,
    versao: number,
    formato: FormatoDocumento,
  ): Promise<DocumentoDownloadResponse> {
    const q = new URLSearchParams();
    q.set("formato", formato);
    return apiFetch(
      url(`/finance/proposals/${propostaId}/documents/${versao}/download?${q.toString()}`),
    );
  },

  atualizarFreelancerDaProposta(
    propostaId: string,
    freelancerId: string,
    body: PatchFreelancerDaPropostaBody,
  ): Promise<PropostaFreelancerDocumentos> {
    return apiFetch(url(`/finance/proposals/${propostaId}/freelancers/${freelancerId}`), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  atualizarAgenciaDaProposta(
    propostaId: string,
    body: PatchAgenciaDaPropostaBody,
  ): Promise<PropostaAgenciaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}/agencia`), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  getInvitePreview(propostaId: string, freelancerId: string, edicao = false): Promise<PropostaPreviewResponse> {
    return apiFetch(
      url(`/finance/proposals/${propostaId}/invites/${freelancerId}/preview${edicao ? "?edicao=true" : ""}`),
    );
  },

  getInviteDocumentoDownload(
    inviteId: string,
    versao: number,
    formato: FormatoDocumento,
  ): Promise<DocumentoDownloadResponse> {
    const q = new URLSearchParams();
    q.set("formato", formato);
    return apiFetch(url(`/finance/invites/${inviteId}/documents/${versao}/download?${q.toString()}`));
  },

  listarOsHistorico(params: ListarOsParams): Promise<OsHistoricoResponse> {
    const q = new URLSearchParams();
    for (const [chave, valor] of Object.entries(params)) {
      if (valor !== undefined && valor !== "") q.set(chave, String(valor));
    }
    return apiFetch(url(`/finance/os?${q.toString()}`));
  },

  getOrdensServico(propostaId: string): Promise<OrdensServicoPreviaResponse> {
    return apiFetch(url(`/finance/proposals/${propostaId}/ordens-servico`));
  },

  reagruparOrdensServico(propostaId: string, body: AgrupamentoOsBody): Promise<OrdensServicoPreviaResponse> {
    return apiFetch(url(`/finance/proposals/${propostaId}/ordens-servico/agrupamento`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  enviarOrdensServico(propostaId: string, body: EnviarOsBody): Promise<EnviarOsResponse> {
    return apiFetch(url(`/finance/proposals/${propostaId}/ordens-servico/enviar`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  sincronizarOs(): Promise<SincronizarOsResponse> {
    return apiFetch(url("/finance/omie/sync-os"), { method: "POST" });
  },

  cancelarProposta(propostaId: string, motivo: string): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}/cancel`), {
      method: "POST",
      body: JSON.stringify({ motivo }),
    });
  },

  mudarCicloDaProposta(propostaId: string, acao: AcaoDoCiclo, body: CicloBody): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/proposals/${propostaId}/ciclo/${acao}`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  mudarCicloDoInvite(inviteId: string, acao: AcaoDoCiclo, body: CicloBody): Promise<PropostaDetail> {
    return apiFetch(url(`/finance/invites/${inviteId}/ciclo/${acao}`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  rascunhoClienteOmie(tipo: OmieTipoCadastro, id: string, empresa: OmieEmpresaChave): Promise<RascunhoClienteOmie> {
    return apiFetch(url(`/finance/omie/cadastro/${tipo}/${id}?empresa=${empresa}`));
  },

  cadastrarClienteOmie(tipo: OmieTipoCadastro, id: string, body: CadastrarClienteOmieBody): Promise<ClienteOmieGuardado> {
    return apiFetch(url(`/finance/omie/cadastro/${tipo}/${id}`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  buscarCep(cep: string): Promise<EnderecoDoCep> {
    return apiFetch(url(`/finance/omie/cep/${cep}`));
  },

  enviarDocumentoAoOmie(clienteId: string): Promise<ClienteOmieGuardado> {
    return apiFetch(url(`/finance/omie/clientes/${clienteId}/documento/enviar`), { method: "POST" });
  },

  trazerDocumentoDoOmie(clienteId: string): Promise<ClienteOmieGuardado> {
    return apiFetch(url(`/finance/omie/clientes/${clienteId}/documento/trazer`), { method: "POST" });
  },
};
